import { Shape, ShapeType } from '@/services/shape';
import { GlobalConfig } from '@/services/gloabl.config';
import {
  canUse,
  canUseDataSource,
  canUseDataSourceByShape
} from '@/utils/can-use.utils';
import { ResourceKey } from '@/services/shape/attributes';
import {
  getWatchfaceDeviceRadius,
  WatchfaceDeviceType,
  useWatchfaceRenderAttr,
  LayoutRenderAttr,
  useShapeManage
} from '@/hooks';
import { useImagesStore } from '@/store/use-images-store';
import { dataSourceWholeItems } from '@/resources/data-source';
import { ShapeUtils } from '@/services/shape/shape-util';
import { isSourceKeyFormat } from '../number';
import { Description } from '@/types/watchface';
import { validShapeEnabled } from './validShape';
import { getImageArrayIndexs } from '@/hooks/use-image-array';
import { useIfttt } from '@/hooks/use-ifttt';
import { useWatchfaceStore } from '@/store/use-watchface-store';
import { getCharLength } from '../richtext.util';
import { rotatedRectExtrema } from '../calculate-rotation';
import KonvaService from '@/services/konva';
import { useRuntimeStore } from '@/store/use-runtime-store';
import { round } from 'lodash-es';

export interface WatchfaceInvalidItem {
  shapeId: string;
  themeId: string;
  colorId?: string;
  errMsg: string;
}

const invalidRes: WatchfaceInvalidItem[] = [];

const deleteInvalidResources = (shape: Shape) => {
  const { resources } = shape.attrs;
  const resourceNames = Object.keys(resources ?? {});
  if (!resourceNames.length) return;

  for (const _resourceName of resourceNames) {
    const resourceName = _resourceName as ResourceKey;
    const colorIds = Object.keys(resources?.[resourceName] ?? {});
    if (!colorIds.length) continue;
    colorIds.forEach((colorId) => {
      // 去除未上传的资源
      const srcs = resources?.[resourceName]?.[colorId]?.filter(
        (item) => !!item
      );
      // 依次验证每个资源是否存在
      const values = shape
        .getResources({
          resourceName,
          colorGroup: colorId,
          base64: true
        })
        .filter((item) => !!item);

      if (srcs?.length && srcs.length != values.length) {
        // 代表资源损坏，需要删除
        shape.deleteResource({
          resourceName,
          colorGroup: colorId
        });
      }
    });
  }
};

const validArrayImageCount = (data: {
  theme: StoreThemeItem;
  shape: Shape;
  isRecolorMode: boolean;
  deviceType: WatchfaceDeviceType;
}) => {
  const { theme, shape, isRecolorMode, deviceType } = data;

  const themeId = theme.id;
  const shapeId = shape.id;
  if (shape.type !== ShapeType.ImageArray) return;

  const images = shape.attrs.resources?.images ?? {};
  const colorIds = theme.colorGroupTable.map((item) => item.id);
  colorIds.forEach((colorId, index) => {
    // 如果是人像或相册，仅需要验证配色1的资源是否上传
    if (isRecolorMode && index > 0) return;
    // 单一息屏不需要校验其他配色
    if (theme.isSingleAOD && index > 0) return;

    const srcs = images[colorId];
    if (!srcs?.length) {
      invalidRes.push({
        shapeId,
        themeId,
        colorId: theme.isSingleAOD ? '' : colorId,
        errMsg: '组图资源不完整，请上传！'
      });

      return;
    }
    // 校验图组中每个图片是否上传
    const imageIndexs = getImageArrayIndexs(shape, deviceType);
    const emptyIndex = imageIndexs.find((item) => {
      if (item.hidden) return false;
      const { index } = item;
      return !srcs[index];
    });
    if (emptyIndex) {
      invalidRes.push({
        shapeId,
        themeId,
        colorId,
        errMsg: '图组资源 "' + emptyIndex.label + '" 缺失，请上传！'
      });

      return;
    }
  });
};

const validWidgetMaxCount = (
  themeId: string,
  shape: Shape,
  layer: StoreLayerItem
) => {
  if (shape.type !== ShapeType.Widget) return;
  // 验证组内直接子元素不可超过10个
  const maxCount = GlobalConfig.widgetChildrenMaxCount;
  if ((layer?.children?.length ?? 0) > maxCount) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '组内直接子元素不可超过' + maxCount + '个'
    });
  }
};
const validWidgetLayoutNegative = (
  themeId: string,
  shape: Shape,
  layer: StoreLayerItem
) => {
  if (shape.type !== ShapeType.Widget) return;
  // 自动布局可以不考虑坐标
  if (shape.isFlexLayout) return;
  // 验证子元素坐标不可为负数
  layer?.children?.forEach((kid) => {
    const { x = 0, y = 0 } = kid.attrs;
    if (Number(x) < 0 || Number(y) < 0) {
      invalidRes.push({
        shapeId: kid.id,
        themeId,
        errMsg: '组内子元素坐标不可为负数'
      });
    }
  });
};

const validLayoutNegative = (themeId: string, shape: Shape) => {
  const { x, y } = shape.attrs;
  const _x = Number(x);
  const _y = Number(y);
  const minXY = shape.minXY;
  const maxXY = shape.maxXY;
  if (_x < minXY || _y < minXY) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '图层坐标不可小于' + minXY
    });
  }
  if (_x > maxXY || _y > maxXY) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '图层坐标不可大于' + maxXY
    });
  }
};

const validSlotEditboxAndPreview = (data: {
  theme: StoreThemeItem;
  slotShape: Shape;
  layer: StoreLayerItem;
  isRecolorMode: boolean;
}) => {
  const { theme, slotShape, layer, isRecolorMode } = data;
  if (slotShape.type !== ShapeType.Slot) return;
  if (
    slotShape.attrs.slotType !== 'widget' ||
    (layer?.children?.length ?? 0) < 1
  )
    return;

  const colorIds = theme.colorGroupTable.map((item) => item.id);
  if (!colorIds.length) return;

  const _valid = (shape: Shape, resourceName: 'editBox' | 'widgetPreview') => {
    colorIds.forEach((colorId, index) => {
      if (isRecolorMode && index > 0) return;
      if (theme.isSingleAOD && index > 0) return;
      const imgSrcs = shape?.attrs?.resources?.[resourceName]?.[colorId];
      if (!imgSrcs?.length) {
        invalidRes.push({
          shapeId: slotShape.id,
          themeId: theme.id,
          colorId: theme.isSingleAOD ? '' : colorId,
          errMsg:
            resourceName == 'editBox' ? '缺少小部件边框图' : '缺少小部件预览图'
        });
      }
    });
  };

  // 多个组 或者  只有一个组，如果“可选无”打开时，也要校验边框图
  if (
    slotShape.children.length > 1 ||
    (slotShape.children.length === 1 && slotShape.attrs.includeEmpty)
  ) {
    // 验证小部件边框图是否上传
    if (slotShape.attrs.sameEditBox) {
      _valid(slotShape, 'editBox');
    } else {
      layer?.children?.forEach((kid) => {
        const _shape = Shape.getShape(kid.id);
        if (_shape) _valid(_shape, 'editBox');
      });
    }
  }

  // 验证小部件预览图是否上传
  layer?.children?.forEach((kid) => {
    const _shape = Shape.getShape(kid.id);
    if (_shape) _valid(_shape, 'widgetPreview');
  });
};

const validTextContent = (themeId: string, shape: Shape) => {
  if (shape.type !== ShapeType.Text) return;
  const usingDataSource = !!shape.attrs.strings?.some((item) => item.isSource);
  if (!usingDataSource) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '文本未绑定数据，请绑定'
    });
  }
};

const validWidgetGapNegatice = (themeId: string, shape: Shape) => {
  if (shape.type == ShapeType.Widget) {
    if ((shape.attrs.flexDirection ?? 'disabled') == 'disabled') return;
    if (Number(shape.attrs.gap ?? 0) < 0) {
      const { valid, msg } = canUse('widget-gap-negative');
      if (!valid) {
        invalidRes.push({
          shapeId: shape.id,
          themeId,
          errMsg: msg ?? ''
        });
      }
    }
  }
  if (shape.type == ShapeType.ImageArray) {
    if (shape.widgetImageArray) {
      if (
        Number(shape.attrs.widgetSpace ?? 0) < 0 &&
        !canUse('widget-gap-negative').valid
      ) {
        invalidRes.push({
          shapeId: shape.id,
          themeId,
          errMsg: '图组间距不可设置为负数'
        });
      }
    }
  }
};
const removeVerticalAlignIfNotSupport = (themeId: string, shape: Shape) => {
  if (shape.type !== ShapeType.Text) return;
  if (!canUse('verticalAlign').valid && shape.attrs.verticalAlign) {
    shape.setAttrs({
      verticalAlign: undefined
    });
  }
};
const validSlotChildrenCount = (
  themeId: string,
  shape: Shape,
  layer: StoreLayerItem,
  deviceType: WatchfaceDeviceType
) => {
  if (shape.type !== ShapeType.Slot) return;
  const { getWidgetMaxCount } = useShapeManage();
  const maxWidgetCounts = getWidgetMaxCount(deviceType);

  if ((layer?.children?.length ?? 0) > maxWidgetCounts) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '容器内子元素数量不可超过' + maxWidgetCounts + '个'
    });
  }
};
const validSlotDualtime = (
  themeId: string,
  shape: Shape,
  layer: StoreLayerItem
) => {
  if (shape.type !== ShapeType.Slot) return;
  if (shape.attrs.slotType != 'dualTime') return;
  const widget = layer?.children?.[0];
  if ((layer?.children?.length ?? 0) != 1 || !widget) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '容器绑定双时区后，有且只能有一个组'
    });
    return;
  }
};
const validLetterSpaceNavigate = (themeId: string, shape: Shape) => {
  if (shape.type !== ShapeType.Text) return;
  const { letterSpace = 0 } = shape.attrs;
  if (Number(letterSpace) < 0) {
    const { valid, msg } = canUse('letter-space-negative');
    if (!valid) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: msg ?? ''
      });
    }
  }
};

/**
 * 判断是否可用timeHour24H数据源
 * @param themeId
 * @param shape
 * @returns
 */
const validTimeHour24_12H = (shape: Shape, deviceType: WatchfaceDeviceType) => {
  const { sourceHourMode } = shape.attrs;
  if (!sourceHourMode || sourceHourMode == 'system') return;
  if (!canUseDataSource('timeHour12H', deviceType)) {
    shape.setAttrs({ sourceHourMode: 'system' });
  }
};
/**
 * 判断是否超过最大值
 * @param themeId
 * @param shape
 * @returns
 */
const validNumberMaxValue = (themeId: string, shape: Shape) => {
  const { rotation = 0 } = shape.attrs;
  if (rotation > 720) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '旋转角度不可超过720度'
    });
  }
};

/**
 * 校验同一图层在不同配色下的资源尺寸是否一致
 */
const validImageSizeBetweenColors = (themeId: string, shape: Shape) => {
  const imageStore = useImagesStore();
  const { resources } = shape.attrs;
  const resourceNames = Object.keys(resources ?? {}) as ResourceKey[];
  if (!resourceNames.length || !resources) return;
  for (const resourceName of resourceNames) {
    // 如果是预览图 && 边框图，跳过
    if (
      ['widgetPreview', 'editBox', 'rivePreview', 'videoPreview'].includes(
        resourceName
      )
    )
      continue;
    const colorIds = Object.keys(resources[resourceName] ?? {});
    if (!colorIds.length) continue;
    let w1: number | undefined;
    let h1: number | undefined;
    for (const colorId of colorIds) {
      const imgSrcs = resources[resourceName]?.[colorId];
      if (!imgSrcs || !imgSrcs.length) continue;
      // 验证同一资源下所有图片必须大小一致
      let w2: number | undefined;
      let h2: number | undefined;
      for (const imgSrc of imgSrcs) {
        if (!imgSrc) continue;
        const { width = 0, height = 0 } = imageStore.imagesMap[imgSrc] ?? {};
        if (w2 == undefined) w2 = width;
        if (h2 == undefined) h2 = height;

        // 是否验证图组资源中各个图片大小是否一致？
        // if (width != w2 || height != h2) {
        //   invalidRes.push({
        //     shapeId: shape.id,
        //     themeId,
        //     colorId,
        //     errMsg: '图片资源尺寸不一致'
        //   });
        // }
      }
      if (
        w2 != undefined &&
        h2 != undefined &&
        w1 == undefined &&
        h1 == undefined
      ) {
        w1 = w2;
        h1 = h2;
      }
      if (w1 == undefined || h1 == undefined) continue;
      if (w1 != w2 || h1 != h2) {
        invalidRes.push({
          shapeId: shape.id,
          themeId,
          colorId,
          errMsg: '该配色下图片资源与前几个配色尺寸不一致'
        });
      }
    }
  }
};
/**
 * 校验图层资源是否上传
 */
const validShapeResources = (data: {
  theme: StoreThemeItem;
  shape: Shape;
  isRecolorMode: boolean;
  watchOS: Description['watchOS'];
}) => {
  const { theme, shape, isRecolorMode } = data;

  const themeId = theme.id;
  const type = shape.type;
  // 图组在validArrayImageCount中验证，在此不重复校验
  if (type == ShapeType.ImageArray) return;
  const { resources, spriteType = 'sprite', videoSrc, videoColorData } = shape.attrs;
  if (spriteType == 'video' && data.watchOS !== 'vela') {
    // 检查是否有视频资源：优先检查当前主题的 videoColorData，fallback 到任意已有样式，最后回退到全局 videoSrc
    const currentColorGroup = theme.colorGroupTable?.[0]?.id ?? '';
    const hasVideo =
      (currentColorGroup && videoColorData?.[currentColorGroup]?.videoSrc) ||
      (videoColorData
        ? Object.values(videoColorData).some((v) => !!v?.videoSrc)
        : false) ||
      videoSrc;
    if (!hasVideo) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: '缺少视频资源，请上传'
      });
      return;
    }
  }
  const _getResourceNames = (type: ShapeType): ResourceKey[] => {
    if (type == ShapeType.Image) {
      return ['pointer0'];
    }
    if (type == ShapeType.ImageArray) {
      return ['images'];
    }
    if (type == ShapeType.ProgressBar) {
      return ['progress'];
    }
    if (type == ShapeType.Sprite && spriteType == 'sprite') {
      return ['images'];
    }
    const spriteItems = shape.getSpriteMulitItems(true) ?? [];
    // 视频必须上传视频对应的预览图
    // 视频表盘必须上传预览图
    if (
      (type == ShapeType.Sprite && spriteType == 'video') ||
      type == ShapeType.VideoWatchface
    ) {
      const videoResourceNames: ResourceKey[] = ['videoPreview'];
      if (spriteItems.length > 1) {
        spriteItems.map((item) => {
          if (item.videoPreviewName)
            videoResourceNames.push(item.videoPreviewName as 'videoPreview');
        });
      }
      return videoResourceNames;
    }
    if (type == ShapeType.Rive) {
      return ['rivePreview'];
    }
    return [];
  };
  const resourceNames = _getResourceNames(type) ?? [];
  if (!resourceNames.length) return;

  for (const resourceName of resourceNames) {
    const colorIds = theme.colorGroupTable.map((item) => item.id);
    if (!colorIds.length) continue;
    colorIds.forEach((colorId, index) => {
      // 校验预览图是否上传不校验其他配色
      if (isRecolorMode && index > 0) return;
      // 如果是视频预览图，不需要验证第二个配色
      if (['videoPreview'].includes(resourceName) && index > 0) return;
      // 单一息屏不需要校验其他配色
      if (theme.isSingleAOD && index > 0) return;

      // 验证非配色1资源是否上传，需要考虑是否人像表盘
      // 仅判断第一个配色，其他配色暂不进行验证
      // 解决https://jira.n.xiaomi.com/browse/BPGJ-689，验证所有配色
      // if (index > 0) return;
      const imgSrcs = resources?.[resourceName]?.[colorId];

      const errMsg =
        resourceName.indexOf('videoPreview') > -1
          ? '缺少视频预览图，请上传'
          : resourceName == 'rivePreview'
          ? '缺少Rive预览图，请上传'
          : '缺少必要的资源，请上传';

      if (!imgSrcs || !imgSrcs.length) {
        invalidRes.push({
          shapeId: shape.id,
          themeId,
          colorId: theme.isSingleAOD ? '' : colorId,
          errMsg
        });
      }
    });
  }
};

const validDataSource = (
  themeId: string,
  shape: Shape,
  deviceType: WatchfaceDeviceType,
  theme: StoreThemeItem
) => {
  const keys = [...shape.soueceKeys];
  // 验证图组和进度条是否绑定数据源
  const type = shape.type;
  if ([ShapeType.ImageArray, ShapeType.ProgressBar].includes(type)) {
    if (!keys.length) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: '未绑定数据源，无法导出'
      });
    }
  }
  if (!keys.length) return;

  // 获取所有的数据源key
  if ([ShapeType.Image, ShapeType.ProgressBar].includes(type)) {
    const { valueStart, valueEnd } = shape.attrs;
    const isStartSource = !!(valueStart && isSourceKeyFormat(valueStart));
    const isEndSource = !!(valueEnd && isSourceKeyFormat(valueEnd));

    // 验证指针或进度条是否可绑定最大或最小值为数据源
    if (isEndSource || isStartSource) {
      if (!canUse('data-source-bind-max-min-value-support', deviceType).valid) {
        invalidRes.push({
          shapeId: shape.id,
          themeId,
          errMsg: '最小值或最大值不支持数据源，请修改'
        });
      }
    }
    // 用于验证数据是否支持
    if (isStartSource) keys.push(valueStart + '');
    if (isEndSource) keys.push(valueEnd + '');
  }
  // 验证是否使用该设备不支持的数据源
  if (keys.some((key) => !canUseDataSource(key, deviceType as 'N62'))) {
    const invalidKey = keys.find(
      (key) => !canUseDataSource(key, deviceType as 'N62')
    );
    if (invalidKey) {
      const keyVal = dataSourceWholeItems.find(
        (item) => item.key == invalidKey
      )?.label;
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: `不可使用"${keyVal}"数据源，请修改`
      });
    }
  }

  // 验证图层绑定的数据源是否正常
  const invalidSourceKey = shape.soueceKeys.find((key) => {
    return (
      key &&
      !canUseDataSourceByShape({
        sourceKey: key,
        shapeType: ShapeUtils.toDataSourceType(shape) ?? 'image',
        deviceType,
        themeType: theme.type == 'AOD' ? 'AOD' : 'normal'
      })
    );
  });

  if (invalidSourceKey) {
    const keyLabel = dataSourceWholeItems.find(
      (item) => item.key == invalidSourceKey
    )?.label;

    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: `在${
        theme.type == 'normal' ? '亮屏' : '息屏'
      }下，不可使用"${keyLabel}"数据源，请修改`
    });
  }
};
const validTextAttrs = (themeId: string, shape: Shape) => {
  if (shape.type == ShapeType.Text) {
    if (!shape.attrs.fontId || !shape.attrs.fontWeight) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: '字体或字重属性未设置'
      });
    }
  }
  if (shape.type == ShapeType.TextWatchface) {
    const { texts = [], textType } = shape.attrs;
    if (!texts?.every((item) => !!item.length)) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: '文字列表内容不可为空'
      });
    }
    if (getCharLength(texts?.join('') || '') > 255 - texts.length) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg:
          textType === 'slogan'
            ? '标语文字总长度超过(255-N)英文字符限制'
            : '星期文字总长度超过248英文字符限制'
      });
    }
  }
};

const validProgressBar = (themeId: string, shape: Shape) => {
  if (shape.type !== ShapeType.ProgressBar) return;
  const { valid, msg } = canUse('shape-progress-bar');
  if (!valid) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: msg ?? ''
    });
  }
};

const validPointerWithinRange = (
  themeId: string,
  shape: Shape,
  watchfaceSize: { width: number; height: number }
) => {
  if (!shape.isPointerImage) return;
  const { x = 0, y = 0, w = 0, h = 0 } = shape.attrs;
  const { pivotX = w / 2, pivotY = h / 2 } = shape.attrs;
  // 旋转圆心坐标
  let absoluteX = Number(x) + Number(pivotX);
  let absoluteY = Number(y) + Number(pivotY);

  let parent = shape.parent;
  while (parent) {
    const { x = 0, y = 0 } = parent.attrs;
    absoluteX += Number(x);
    absoluteY += Number(y);
    parent = parent.parent;
  }
  // 获取指针旋转圆心
  const r = ShapeUtils.getPointerRadius(shape, true);

  const left = absoluteX - r;
  const right = absoluteX + r;
  const top = absoluteY - r;
  const bottom = absoluteY + r;

  const { width, height } = watchfaceSize;
  const valid = left >= 0 && right <= width && top >= 0 && bottom <= height;

  if (!valid) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '指针图片过大，旋转时会超出表盘范围，请缩小图片'
    });
  }
};

/**
 * @param themeId string
 * @param shape Shape
 * @param deviceType WatchfaceDeviceType
 * @description 检查是否支持组嵌套
 */
const validArrNest = (
  themeId: string,
  shape: Shape,
  deviceType: WatchfaceDeviceType,
  parent: StoreLayerItem | undefined
) => {
  if (deviceType !== 'M66') return;
  if (shape.type !== ShapeType.Widget) return;

  if (parent?.type == ShapeType.Widget) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '组不支持嵌套'
    });
  }
};

// 验证组或容器不可为空
const validSlotOrWidgetIsNotEmpty = (
  themeId: string,
  shape: Shape,
  layer: StoreLayerItem
) => {
  const { type } = shape;
  if ([ShapeType.Slot, ShapeType.Widget].includes(type)) {
    if (!layer?.children?.length) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: type == ShapeType.Slot ? '容器不可为空' : '组不可为空'
      });
    }
  }
};

/**
 * 息屏下不可使用容器或组
 * @param themeId
 * @param shape
 * @param layer
 */
const validAODSlotOrWidges = (theme: StoreThemeItem, shape: Shape) => {
  if (theme.type == 'AOD') {
    const { type } = shape;
    if ([ShapeType.Slot, ShapeType.Widget].includes(type)) {
      invalidRes.push({
        shapeId: shape.id,
        themeId: theme.id,
        errMsg: '息屏下不可使用容器或组'
      });
    }
  }
};

/**
 * 验证Rive图层
 * @param shape
 * @param layer
 */
const validRive = (
  theme: StoreThemeItem,
  shape: Shape,
  deviceType: WatchfaceDeviceType,
  watchOS: Description['watchOS']
) => {
  if (shape.type != ShapeType.Rive) return;

  // Rive图层仅在亮屏下可用
  if (theme.type != 'normal') {
    invalidRes.push({
      shapeId: shape.id,
      themeId: theme.id,
      errMsg: 'Rive图层仅在亮屏下可用'
    });
  }
  // 验证图层顺序：仅能在顶层或底层
  const index = theme.children.findIndex((item) => item.id == shape.id);
  if (index != 0 && index != theme.children.length - 1) {
    invalidRes.push({
      shapeId: shape.id,
      themeId: theme.id,
      errMsg: 'Rive图层仅能在顶层或底层'
    });
  }

  // 验证rive数量
  const count = theme.children.filter(
    (item) => item.type == ShapeType.Rive
  ).length;
  if (count > 1) {
    invalidRes.push({
      shapeId: shape.id,
      themeId: theme.id,
      errMsg: 'Rive图层最多只能有一个'
    });
  }

  const colorIds = theme.colorGroupTable.map((item) => item.id);
  // 如果是android，需要上传高级文件

  // 验证rive图层是否上传rive文件
  if (!shape.attrs.riveFile) {
    if (!colorIds.length)
      invalidRes.push({
        shapeId: shape.id,
        themeId: theme.id,
        errMsg: '请上传rive文件'
      });
    else
      colorIds.forEach((colorId) => {
        invalidRes.push({
          shapeId: shape.id,
          themeId: theme.id,
          colorId,
          errMsg: '请上传rive文件'
        });
      });
  }

  // O61/Q61rive文件数量为1个时，需要检查智能类型是否为智能&轻智能，否则报错
  if (deviceType == 'O61' || deviceType == 'Q61') {
    if (watchOS == 'android' && shape.attrs.riveFileCounts == 1) {
      if (!colorIds.length)
        invalidRes.push({
          shapeId: shape.id,
          themeId: theme.id,
          errMsg: '智能类型勾选错误，请勾选智能&轻智能，或正确上传rive文件'
        });
      else
        colorIds.forEach((colorId) => {
          invalidRes.push({
            shapeId: shape.id,
            themeId: theme.id,
            colorId,
            errMsg: '智能类型勾选错误，请勾选智能&轻智能，或正确上传rive文件'
          });
        });
    }
  }

  // 非必填，不进行校验
  // if (watchOS == 'android') {
  //   if (!shape.attrs.riveFileExt) {
  //     invalidRes.push({
  //       shapeId: shape.id,
  //       themeId: theme.id,
  //       errMsg: '请上传rive高级文件'
  //     });
  //   }
  // }
};

/**
 * 验证组跳转应用自定义名称
 * @param themeId string
 * @param shape Shape
 */
const validCustomJumpAppName = (themeId: string, shape: Shape) => {
  if (shape.type !== ShapeType.Widget) return;

  const { jumpApp, jumpAppCustomName } = shape.attrs;

  // 只有当跳转应用为 customName 时才需要验证
  if (jumpApp !== 'customName') return;

  // 自定义名称不能为空
  if (!jumpAppCustomName || jumpAppCustomName.trim() === '') {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '跳转应用选择"自定义名称"时，自定义名称不能为空'
    });
    return;
  }

  // 验证自定义名称格式（只允许字母、中文、数字和点号）
  const validPattern = /^[a-zA-Z0-9.\u4e00-\u9fff]*$/;
  if (!validPattern.test(jumpAppCustomName)) {
    invalidRes.push({
      shapeId: shape.id,
      themeId,
      errMsg: '自定义名称仅支持输入字符、数字、特殊字符仅支持"."'
    });
  }
};

/**
 * 验证Ifttt动画
 * @param shape
 * @param layer
 */
const validIfttt = (theme: StoreThemeItem, shape: Shape) => {
  const { ifttts } = theme;
  if (!ifttts || !ifttts.length) return;

  // 过滤当前图层的ifttt
  const validIfttts = ifttts.filter((ifttt) => {
    return (
      ifttt.shapeId === shape.id &&
      (Shape.getShape(ifttt.shapeId)?.canUseIFTTT ? true : false)
    );
  });
  const { compareTypeOptions, animationTypeOptions } = useIfttt();

  for (const ifttt of validIfttts) {
    const { condition, animations } = ifttt;

    const hasInvalidCondition = condition.children.some((subCondition) => {
      return (
        compareTypeOptions.find((item) => item.value === subCondition.type)
          ?.disabled || false
      );
    });
    if (hasInvalidCondition) {
      invalidRes.push({
        shapeId: shape.id,
        themeId: theme.id,
        errMsg: '存在当前表盘不支持的触发方式'
      });
    }

    const hasInvalidAnimation = animations.some((animation) => {
      return (
        animationTypeOptions.find((item) => item.value === animation.type)
          ?.disabled || false
      );
    });
    if (hasInvalidAnimation) {
      invalidRes.push({
        shapeId: shape.id,
        themeId: theme.id,
        errMsg: '存在当前表盘不支持的动画效果'
      });
    }

    if (theme.type === 'AOD' && !canUse('animation-in-AOD').valid) {
      invalidRes.push({
        shapeId: shape.id,
        themeId: theme.id,
        errMsg: '当前设备息屏不支持IFTTT动效'
      });
      return;
    }
  }
};

/**
 * 验证元素位置
 * @param themeId string
 * @param shape Shape
 * @param watchSize { width: number; height: number }
 */
const validShapePosition = (
  themeId: string,
  shape: Shape,
  layoutRenderAttrs: LayoutRenderAttr[],
  watchSize: { width: number; height: number }
) => {
  // Widget 是逻辑容器，其子元素会被单独校验位置。
  // Widget 的 w/h 可能因指针旋转区域或 syncWidgetSize 撑大而超出表盘，这是合理的，跳过校验。
  if (shape.type === ShapeType.Widget) return;

  // 部分表盘/复杂功能不支持xy为负布局
  // 组内子元素强制走宽松校验：指针等元素可能和表盘一样大，成组后偏移几像素就超出，
  // 只拦截完全在画布外的情况，允许部分超出
  const isInWidget = shape.parent?.type === ShapeType.Widget;
  const isNegativeLayout =
    isInWidget || (canUse('layout-negative')?.valid && shape.type !== ShapeType.AppWidget);

  let shapeLayoutRenderAttr = layoutRenderAttrs.find(
    (item) => item.id === shape.id
  );

  // 指针多象限元素会有多个渲染项，需获取有渲染尺寸的项
  if (shape.attrs.pointerRegionNum && shape.attrs.pointerRegionNum > 1) {
    shapeLayoutRenderAttr = layoutRenderAttrs.find(
      (item) => item.id === shape.id && item.size.width > 0
    );
  }

  if (!shapeLayoutRenderAttr) return;

  const { size, posi } = shapeLayoutRenderAttr;
  let { width = 0, height = 0 } = size;
  if (!width || !height) return;
  // 容器无法修改尺寸，以内部元素最大尺寸为主
  if (shape.type === ShapeType.Slot) {
    shape.layer?.children?.forEach((item) => {
      width = Math.max(width, item.attrs.w);
      height = Math.max(height, item.attrs.h);
    });
  }
  if (
    shape.type === ShapeType.Rive ||
    shape.type === ShapeType.Sprite
  ) {
    width = shape.attrs.w || 0;
    height = shape.attrs.h || 0;
  }
  if (shape.type === ShapeType.Text) {
    // jira单 1082 文本+排版时，文本宽高统一以属性面板中最终显示的 w/h 为准 值为实时获取的宽高
    const getRuntimeSize = () => useRuntimeStore().shapeState(shape.id);
    const runtime = getRuntimeSize();
    // 优先使用运行时尺寸，获取不到时保留预渲染尺寸（跨样式校验时当前画布可能未渲染该样式的文本）
    if (runtime?.width && runtime?.height) {
      width = runtime.width;
      height = runtime.height;
    }
  }

  let { x = 0 } = posi;
  const { y = 0 } = posi;
  const { align = 'left', rotation = 0, style = 'normal' } = shape.attrs;
  const AlignXFactor = {
    left: 0,
    center: 0.5,
    right: 1
  };
  if (style === 'normal' && shape.type === ShapeType.ImageArray) {
    x = x - width * AlignXFactor[align];
  }

  const { top, bottom, right, left } = rotatedRectExtrema(
    x,
    y,
    width,
    height,
    rotation
  );

  if (isNegativeLayout) {
    const isOutside =
      right.x < 0 ||
      left.x > watchSize.width ||
      bottom.y < 0 ||
      top.y > watchSize.height;
    if (isOutside) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: '元素超出当前表盘分辨率的范围不合理'
      });
    }
  } else {
    const isInside =
      left.x >= 0 &&
      top.y >= 0 &&
      right.x <= watchSize.width &&
      bottom.y <= watchSize.height;
    if (!isInside) {
      invalidRes.push({
        shapeId: shape.id,
        themeId,
        errMsg: '元素超出当前表盘分辨率的范围不合理'
      });
    }
  }
};
/**
 * 组成容器时，组内元素不得超过当前容器的尺寸
 */
const validShapeSize = (
  themeId: string,
  shape: Shape,
  layoutRenderAttrs?: LayoutRenderAttr[]
) => {
  // 先确定父元素尺寸：优先使用预渲染尺寸（含子元素实际占位），其次使用 attrs.w/h
  // 成组后，父元素 attrs.w/h 可能为成组时的初始值，而固定尺文本的渲染尺寸会随数据源变化，
  // 导致子元素渲染尺寸大于父 attrs，产生误报。预渲染尺寸由子元素实际边界计算，更准确
  let w = shape.attrs.w ?? 0;
  let h = shape.attrs.h ?? 0;
  const { widgetIdToDisplay } = shape.attrs;
  if (layoutRenderAttrs) {
    const parentLayoutAttr = layoutRenderAttrs.find(
      (item) => item.id === shape.id
    );
    if (parentLayoutAttr?.size?.width && parentLayoutAttr?.size?.height) {
      w = Math.max(parentLayoutAttr.size.width, w);
      h = Math.max(parentLayoutAttr.size.height, h);
    }
  }
  const shapeType = shape.type;
  const service = KonvaService.getIns();
  for (const childShape of shape.children) {
    const childShapeType = childShape.type;
    if (childShapeType === ShapeType.Widget) {
      // 处理组内有组的情况，父元素非容器时直接调用递归校验，
      // 否则通过 widgetIdToDisplay 判断，只校验当前显示的组。
      // 非当前显示的组没有预渲染数据（layoutRenderAttrs 中找不到），
      // 强制校验会因 Math.max 撑大的父容器尺寸与旋转 AABB 不匹配导致误报。
      const shouldRecurse =
        shapeType !== ShapeType.Slot || widgetIdToDisplay === childShape.id;
      if (shouldRecurse) {
        validShapeSize(themeId, childShape, layoutRenderAttrs);
      }
      continue;
    } else {
      // 当前正在使用的组内元素进入此判断
      // 优先使用预计算的渲染尺寸（每个主题单独渲染得到，避免依赖当前画布状态）
      // 因为 getClientRect 依赖于当前画布渲染的样式，如果画布当前渲染的是其他样式，
      // 会导致获取到错误的尺寸
      let width = 0;
      let height = 0;
      let childLayoutAttr: LayoutRenderAttr | undefined;
      if (layoutRenderAttrs) {
        childLayoutAttr = layoutRenderAttrs.find(
          (item) => item.id === childShape.id
        );
        if (childLayoutAttr) {
          width = childLayoutAttr.size.width;
          height = childLayoutAttr.size.height;
        }
      }
      // // 如果没有预渲染尺寸，再回退到 Konva 的实时尺寸
      // // 注意：这可能获取到当前画布渲染的样式尺寸，而不是正在验证的样式尺寸
      if (!width || !height) {
        // 当传入了 layoutRenderAttrs（即通过 getWatchfaceRenderAttr 预渲染了该 theme）
        // 但仍找不到子元素的渲染尺寸时，说明该子元素不在当前渲染结果中（如非显示组的子元素）。
        // 此时不应回退到 getClientRect，因为画布可能渲染的是其他 theme，会拿到错误尺寸导致误报。
        // 只有在没有预渲染数据时（layoutRenderAttrs 为空），才回退到 getClientRect。
        if (layoutRenderAttrs?.length) {
          continue;
        }
        const { width: rectW, height: rectH } =
          service?.getClientRect(childShape.id, true) ?? {};
        width = rectW ?? 0;
        height = rectH ?? 0;
      }

      // 指针特性特殊处理：两象限或四象限时，pointer1/pointer2/pointer3 是 pointer0 的镜像
      // 它们的尺寸应该和 pointer0 相同，但可能在某些情况下获取到的尺寸不同
      // 因此对于指针特性，统一使用 attrs.w 和 attrs.h（配置的尺寸）或 pointer0 的尺寸
      if (
        childShape.isPointerImage &&
        childShape.attrs.pointerRegionNum &&
        childShape.attrs.pointerRegionNum > 1
      ) {
        // 优先使用配置的尺寸（attrs.w/h），这些对所有象限都相同
        const { w: pointerW, h: pointerH } = childShape.attrs;
        if (pointerW && pointerH) {
          width = pointerW;
          height = pointerH;
        } else {
          // 如果没有配置尺寸，尝试获取 pointer0 的实际图片尺寸
          // 从 resources 中获取第一个配色 ID
          const resources = childShape.attrs.resources;
          const pointer0Resources = resources?.pointer0;
          if (pointer0Resources) {
            const colorIds = Object.keys(pointer0Resources);
            if (colorIds.length > 0) {
              const firstColorId = colorIds[0];
              const pointer0Size = childShape.getImageSize(
                'pointer0',
                firstColorId
              );
              if (pointer0Size?.width && pointer0Size?.height) {
                width = pointer0Size.width;
                height = pointer0Size.height;
              }
            }
          }
        }
      }
      const { x, y, align, style, rotation } = childShape.attrs;
      // 动效组的子元素偏移值用 0 计算（与容器子元素一致，对齐左上角）
      const isWidgetConditionGroup = (shape.attrs as any).widgetConditions?.length > 0;
      if (w && h && width && height) {
        // 根据 align 计算子元素左上角的实际坐标
        let actualX: number;
        let actualY: number;
        // 如果有旋转文本 旋转角度不能为0 那么直接使用预渲染的坐标 去和组的大小做对比
        if (isWidgetConditionGroup) {
          actualX = 0;
          actualY = 0;
        } else if (childLayoutAttr?.posi && rotation != 0) {
          const { offsetX, offsetY, x: posiX, y: posiY } = childLayoutAttr.posi;
          actualX = offsetX ?? posiX ?? x ?? 0;
          actualY = offsetY ?? posiY ?? y ?? 0;

          // 下面逻辑影响较大，先对图组类型进行优化
          if (childShapeType === ShapeType.ImageArray) {
            // 居中/右对齐的图组：offsetX 是成组时基于当时渲染宽度计算的锚点位置，
            // 但 width 是当前预渲染宽度。Widget.setSize() 对此类元素将 offsetX 归零，
            // 即认为内容从 x=0 开始、宽度为 width。验证时也保持一致。
            if (align && align !== 'left') {
              actualX = 0;
            }

            // 矩形左上角 (actualX, actualY)，宽 width，高 height
            // 以 (actualX, actualY) 为左上角，顺时针旋转 rotation°
            // 旋转中心为 (0, actualY)（与 Widget.setSize 归零逻辑一致）
            let drawX = align && align !== 'left' ? 0 : actualX;
            let drawY = actualY;
            const cos = Math.cos(((rotation ?? 0) * Math.PI) / 180);
            const sin = Math.sin(((rotation ?? 0) * Math.PI) / 180);
            // 旋转前四个角的绝对坐标：左上、右上、右下、左下
            const corners = [
              [actualX, actualY],
              [actualX + width, actualY],
              [actualX + width, actualY + height],
              [actualX, actualY + height]
            ];
            // 绕 (drawX, drawY) 顺时针旋转后的坐标
            const rotatedCorners = corners.map(([px, py]) => {
              const dx = px - drawX;
              const dy = py - drawY;
              return [
                round(drawX + dx * cos - dy * sin),
                round(drawY + dx * sin + dy * cos)
              ];
            });
            // 取旋转后的 AABB 包围盒
            const xs = rotatedCorners.map((p) => p[0]);
            const ys = rotatedCorners.map((p) => p[1]);
            actualX = Math.min(...xs);
            actualY = Math.min(...ys);
            width = Math.max(...xs) - actualX;
            height = Math.max(...ys) - actualY;
          }

        } else {
          actualX = x ?? 0;
          if (align === 'center' && style !== 'arc') {
            actualX = (x ?? 0) - width / 2;
          } else if (align === 'right' && style !== 'arc') {
            // 弧形文本不考虑右对齐
            actualX = (x ?? 0) - width;
          }
          actualY = y ?? 0;

          // 居中/右对齐的图组：attrs.x 是成组时基于当时渲染宽度计算的锚点位置，
          // 但 width 是当前预渲染宽度（随数据源值变化）。两者不匹配时 x - width/2 会产生负值。
          // Widget.setSize() 计算父组尺寸时已将此类元素的 offsetX 归零（从内容左边缘算起），
          // 验证时也需要保持一致，否则会误报。
          if (
            childShapeType === ShapeType.ImageArray &&
            align &&
            align !== 'left'
          ) {
            actualX = 0;
          }
        }

        // 检查左边界、右边界、上边界、下边界
        // 使用 Math.round 处理浮点数精度问题 浮点数出现的原因是元素会有缩放，并允许 0px 的容差
        // 组设置了自动布局时，不判断元素大小是否超过所在组的大小
        const roundedActualX = Math.round(actualX);
        const roundedActualY = Math.round(actualY);
        const roundedWidth = Math.round(width);
        const roundedHeight = Math.round(height);
        const roundedW = Math.round(w);
        const roundedH = Math.round(h);

        // 居中/右对齐的图组：attrs.x 是成组时的锚点位置，与当前预渲染 width 不匹配，
        // 无法可靠判断 x 方向的位置是否超出。但可以检查宽度/高度是否超过父组尺寸。
        const isAlignedImageArray =
          childShapeType === ShapeType.ImageArray &&
          align &&
          align !== 'left';

        // 居中/右对齐 + 旋转的图组：旋转后 AABB 包围盒必然比原尺寸大，
        // Widget.setSize() 对旋转元素的尺寸计算与包围盒逻辑不同，
        // 位置判断不可靠，仅保留宽高不超过父组的基本检查。
        const isAlignedRotatedImageArray =
          isAlignedImageArray && rotation != 0;

        const outOfGroupBounds = isAlignedRotatedImageArray
          ? roundedWidth > roundedW || roundedHeight > roundedH
          : isAlignedImageArray
          ? roundedWidth > roundedW ||
            roundedActualY < 0 ||
            roundedActualY + roundedHeight > roundedH
          : roundedActualX < 0 ||
            roundedActualX + roundedWidth > roundedW ||
            roundedActualY < 0 ||
            roundedActualY + roundedHeight > roundedH;

        if (!shape.isFlexLayout && outOfGroupBounds) {
          console.warn(
            `[validShapeSize] 元素超出组边界诊断:\n` +
            `  子元素: id=${childShape.id}, type=${childShapeType}, name=${childShape.attrs.name ?? ''}\n` +
            `  父组: id=${shape.id}, type=${shape.type}, name=${shape.attrs.name ?? ''}\n` +
            `  父组尺寸: w=${w}, h=${h} (attrs.w=${shape.attrs.w}, attrs.h=${shape.attrs.h})\n` +
            `  子元素渲染尺寸: width=${width}, height=${height}\n` +
            `  子元素位置: actualX=${actualX}, actualY=${actualY} (attrs.x=${x}, attrs.y=${y})\n` +
            `  子元素对齐: align=${align}, rotation=${rotation}, style=${style}\n` +
            `  取整后: pos=(${roundedActualX},${roundedActualY}), size=(${roundedWidth}x${roundedHeight}), group=(${roundedW}x${roundedH})\n` +
            `  超出详情: left=${roundedActualX < 0}, right=${roundedActualX + roundedWidth > roundedW}(${roundedActualX + roundedWidth} > ${roundedW}), top=${roundedActualY < 0}, bottom=${roundedActualY + roundedHeight > roundedH}(${roundedActualY + roundedHeight} > ${roundedH})\n` +
            `  layoutRenderAttr: ${childLayoutAttr ? JSON.stringify({ size: childLayoutAttr.size, posi: childLayoutAttr.posi }) : 'null'}\n` +
            `  isWidgetConditionGroup=${isWidgetConditionGroup}`
          );
          invalidRes.push({
            shapeId: childShape.id,
            themeId,
            errMsg: '元素的大小超过所在组的大小，请调整'
          });
        }
      }
    }
  }
};
/**
 * 验证图层, 返回问题列表
 * @returns Array<{ shapeId: string; errMsg: string }>
 */
export const validWatchfaceThemes = async (data: {
  themes: StoreThemeItem[];
  description: Description;
}) => {
  const { themes, description } = data;
  const isFrom1_5 = description['isFrom1.5'];
  const isRecolorMode = (description.watchfaceType ?? 'normal') != 'normal';
  const deviceType = description.deviceType ?? 'N62';
  const watchfaceStore = useWatchfaceStore();
  const { getWatchfaceRenderAttr } = useWatchfaceRenderAttr();
  // const isAndroidDevice = useWatchfaceAndroid().checkIsAndroid(deviceType);

  invalidRes.length = 0;
  for (const theme of themes) {
    const layers: {
      layer: StoreLayerItem;
      parent: StoreLayerItem | undefined;
      level: number;
    }[] = theme.children.map((item) => {
      return {
        layer: item,
        parent: undefined,
        level: 0
      };
    });

    const layoutRenderAttrs =
      (await getWatchfaceRenderAttr(undefined, {
        themeName: theme.name,
        themeType: theme.type,
        clipRound: getWatchfaceDeviceRadius(deviceType),
        usePreviewPointerData: true,
        slotDisplayAll: true
      })) || [];

    let index = 0;
    while (index < layers.length) {
      const item = layers[index];
      index++;
      const { layer, parent, level = 0 } = item ?? {};
      if (layer?.children?.length)
        layers.push(
          ...layer.children.map((item) => {
            return {
              layer: item,
              parent: layer,
              level: level + 1
            };
          })
        );
      if (!layer) continue;
      const shapeId = layer.id;
      const shape = Shape.getShape(shapeId, layer);
      if (!shape) continue;

      // 在所有验证之前，首先删除资源已损坏的资源
      deleteInvalidResources(shape);

      validShapeResources({
        theme,
        shape,
        isRecolorMode,
        watchOS: description.watchOS
      });
      validArrayImageCount({ theme, shape, isRecolorMode, deviceType });
      validWidgetMaxCount(theme.id, shape, layer);

      // 如果不在widget或slot下，即在layout中，验证坐标是否为负数
      if (!parent) validLayoutNegative(theme.id, shape);

      // 验证widget布局是否为负数
      validWidgetLayoutNegative(theme.id, shape, layer);

      // 非1.5表盘才验证资源，因为1.5表盘也存在没有editBox的情况
      if (!isFrom1_5)
        validSlotEditboxAndPreview({
          theme,
          slotShape: shape,
          layer,
          isRecolorMode
        });

      validWidgetGapNegatice(theme.id, shape);
      removeVerticalAlignIfNotSupport(theme.id, shape);
      validTextContent(theme.id, shape);
      validSlotDualtime(theme.id, shape, layer);
      validSlotChildrenCount(theme.id, shape, layer, deviceType);
      validLetterSpaceNavigate(theme.id, shape);

      // 验证是否使用非法数据源
      validDataSource(theme.id, shape, deviceType, theme);
      // 是否可用12H或24H数据源
      validTimeHour24_12H(shape, deviceType);

      // 仅在非1.5进行校验 验证图片在各个配色下是否一致，需要放到最后进行验证
      if (!isFrom1_5) {
        validImageSizeBetweenColors(theme.id, shape);
      }

      // 验证是否超过最大值
      validNumberMaxValue(theme.id, shape);

      // 验证文本属性是否配置正确
      validTextAttrs(theme.id, shape);

      // 验证进度条是否可用
      validProgressBar(theme.id, shape);

      // 验证是否支持组嵌套
      validArrNest(theme.id, shape, deviceType, parent);

      validSlotOrWidgetIsNotEmpty(theme.id, shape, layer);

      // 验证组跳转应用自定义名称
      validCustomJumpAppName(theme.id, shape);

      // 验证指针是否超出范围
      if (GlobalConfig.validPointerWithinRange.includes(deviceType)) {
        const size = description.size;
        const [w, h] = size.split('x');
        validPointerWithinRange(theme.id, shape, {
          width: Number(w),
          height: Number(h)
        });
      }

      if (deviceType == 'M66') {
        validAODSlotOrWidges(theme, shape);
      }

      // 验证图层是否支持
      // 防止在切换设备类型后，使用不支持的图层
      if (
        !validShapeEnabled({
          shape,
          deviceType,
          themeType: theme.type,
          watchOS: description.watchOS
        })
      ) {
        invalidRes.push({
          shapeId: shape.id,
          themeId: theme.id,
          errMsg: '不支持该图层，请删除'
        });
      }

      // 验证Rive图层
      validRive(theme, shape, deviceType, description.watchOS);

      // 验证ifttt
      validIfttt(theme, shape);

      // 验证元素大小位置是否合法
      validShapePosition(
        theme.id,
        shape,
        layoutRenderAttrs,
        watchfaceStore.size
      );
      // 验证容器内元素大小是否小于所在组大小
      // validShapeSize 已经包括递归处理，所以只处理 level 为 0 的
      if (
        (shape.type == ShapeType.Slot || shape.type == ShapeType.Widget) &&
        level === 0
      ) {
        validShapeSize(theme.id, shape, layoutRenderAttrs);
      }
    }
  }
  return invalidRes;
};
